import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectCanUseGrow, selectClinicId } from "../../store/slices/authSlice";
import { useGetGrowLinksForTransactionsQuery } from "../../store/services/growApi";
import { GROW_ACTIVE_LINK_STATUSES } from "../../firebase";
import GrowLinkPopover from "./GrowLinkPopover";

const LINKABLE = ["expected", "pending", "pending_collection"];

/**
 * useGrowLinksMap — one query for all Grow links behind a list of
 * transactions (Kanban columns, finance list, patient ledger) + a shared
 * popover. Does nothing (no query, `linkFor` → null) when the user cannot
 * use Grow, so legacy clinics pay zero cost.
 *
 * @param {Array<{id:string,status:string,growLinkId?:string,paidVia?:string}>} transactions
 * @returns {{ canUseGrow:boolean, linkFor:(tx)=>object|null, openLink:(link)=>void, popover:JSX|null }}
 */
export default function useGrowLinksMap(transactions = []) {
  const clinicId = useSelector(selectClinicId);
  const canUseGrow = useSelector(selectCanUseGrow);
  const [openedLink, setOpenedLink] = useState(null);

  // Only transactions that can carry a link: unpaid ones, or ones stamped by a link.
  const ids = useMemo(
    () =>
      (transactions || [])
        .filter((t) => t && (LINKABLE.includes(t.status) || t.growLinkId || t.paidVia === "grow"))
        .map((t) => t.id)
        .sort(),
    [transactions]
  );

  const { data: links } = useGetGrowLinksForTransactionsQuery(
    { clinicId, transactionIds: ids },
    { skip: !clinicId || !canUseGrow || ids.length === 0 }
  );

  const byTx = useMemo(() => {
    const map = new Map();
    (links || []).forEach((link) => {
      (link.transactionIds || []).forEach((txId) => {
        const prev = map.get(txId);
        if (!prev) map.set(txId, link);
        else {
          const prevActive = GROW_ACTIVE_LINK_STATUSES.includes(prev.status);
          const curActive = GROW_ACTIVE_LINK_STATUSES.includes(link.status);
          if (curActive && !prevActive) map.set(txId, link);
          else if (curActive === prevActive && new Date(link.createdAt || 0) > new Date(prev.createdAt || 0)) map.set(txId, link);
        }
      });
    });
    return map;
  }, [links]);

  const linkFor = useCallback((tx) => (tx && canUseGrow ? byTx.get(tx.id) || null : null), [byTx, canUseGrow]);
  const openLink = useCallback((link) => setOpenedLink(link), []);

  const popover = openedLink ? (
    <GrowLinkPopover link={openedLink} isOpen onClose={() => setOpenedLink(null)} />
  ) : null;

  return { canUseGrow, linkFor, openLink, popover, links: links || [] };
}
